import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Survey API service
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005/';

const surveyApi = axios.create({
  baseURL: `${API_BASE_URL}api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

const handleApiError = (error) => {
  if (error.response) {
    throw new Error(error.response.data.detail || 'An error occurred');
  } else if (error.request) {
    throw new Error('Network error - please check your connection');
  } else {
    throw new Error('An unexpected error occurred');
  }
};

const surveyService = {
  submitSurvey: async (surveyData) => {
    try {
      const response = await surveyApi.post('survey/submit', surveyData);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  checkSurveyStatus: async (userEmail) => {
    try {
      const response = await surveyApi.get(`/survey/status/${encodeURIComponent(userEmail)}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  getSurveyStats: async () => {
    try {
      const response = await surveyApi.get('/survey/stats');
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  }
};

const Survey = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({
    reading_frequency: '',
    perceived_improvement: '',
    content_difficulty: '',
    biggest_barrier: [], // Changed to array for multiple selection
    most_valuable_feature: [], // Changed to array for multiple selection
    nps_score: 10,
    additional_feedback: ''
  });
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Survey questions configuration
  const questions = [
    {
      id: 'email',
      title: 'Welcome to our PrepLetter Survey!',
      icon: '📊',
      subtitle: 'Help us make PrepLetter even better for you',
      type: 'email',
      question: 'What\'s your email address?',
      placeholder: 'Enter your email address (optional)',
      optional: true
    },
    {
      id: 'reading_frequency',
      title: 'Reading Habits',
      icon: '📖',
      subtitle: 'Understanding your engagement patterns',
      type: 'radio',
      question: 'How often do you read PrepLetter articles?',
      options: [
        { value: 'daily', label: 'Daily', emoji: '🔥' },
        { value: 'few_times_week', label: '3-4 times per week', emoji: '📅' },
        { value: 'weekly', label: 'Once a week', emoji: '📆' },
        { value: 'rarely', label: 'Rarely', emoji: '⏰' },
        { value: 'stopped', label: 'I\'ve stopped reading', emoji: '😔' }
      ]
    },
    {
      id: 'perceived_improvement',
      title: 'Learning Impact',
      icon: '🚀',
      subtitle: 'Measuring your progress',
      type: 'radio',
      question: 'Have you noticed improvement in your coding skills since using PrepLetter?',
      options: [
        { value: 'significant', label: 'Significant improvement', emoji: '🌟' },
        { value: 'some', label: 'Some improvement', emoji: '📈' },
        { value: 'none', label: 'No noticeable change', emoji: '😐' },
        { value: 'too_early', label: 'Too early to tell', emoji: '⏳' }
      ]
    },
    {
      id: 'content_difficulty',
      title: 'Content Level',
      icon: '🎯',
      subtitle: 'Finding the right balance',
      type: 'radio',
      question: 'How do you find the difficulty level of our content?',
      options: [
        { value: 'too_easy', label: 'Too easy', emoji: '😴' },
        { value: 'just_right', label: 'Just right', emoji: '✅' },
        { value: 'too_hard', label: 'Too difficult', emoji: '😰' },
        { value: 'varies', label: 'Varies too much', emoji: '🎢' }
      ]
    },
    {
      id: 'biggest_barrier',
      title: 'Challenges',
      icon: '🚧',
      subtitle: 'What holds you back?',
      type: 'checkbox',
      question: 'What are the barriers to reading PrepLetter daily? (Select all that apply)',
      options: [
        { value: 'time', label: 'Lack of time', emoji: '⏰' },
        { value: 'difficulty', label: 'Content difficulty', emoji: '🧠' },
        { value: 'forgot', label: 'I forget to check', emoji: '🤔' },
        { value: 'lost_interest', label: 'Lost interest', emoji: '😑' },
        { value: 'too_frequent', label: 'Too frequent', emoji: '😑' },
        { value: 'language', label: 'I prefer other coding language', emoji: '😅' },
        { value: 'other', label: 'Other reasons', emoji: '💭' }
      ]
    },
    {
      id: 'most_valuable_feature',
      title: 'Value Proposition',
      icon: '💎',
      subtitle: 'What works best for you?',
      type: 'checkbox',
      question: 'Which parts of PrepLetter are most valuable to you? (Select all that apply)',
      options: [
        { value: 'explanations', label: 'Problem explanations', emoji: '📝' },
        { value: 'solutions', label: 'Solution approaches', emoji: '💡' },
        { value: 'examples', label: 'Code examples', emoji: '💻' },
        { value: 'practice', label: 'Practice problems', emoji: '🏋️' },
        { value: 'applications', label: 'Real-world applications', emoji: '🌍' }
      ]
    },
    {
      id: 'nps_score',
      title: 'Recommendation',
      icon: '🌟',
      subtitle: 'Your honest opinion matters',
      type: 'nps',
      question: 'How likely are you to recommend PrepLetter to a friend?',
      scale: { min: 0, max: 10, labels: ['Not likely', 'Very likely'] }
    },
    {
      id: 'additional_feedback',
      title: 'Final Thoughts',
      icon: '💭',
      subtitle: 'Help us improve',
      type: 'textarea',
      question: 'Any additional feedback or feature requests?',
      placeholder: 'Share your thoughts, suggestions, or what you\'d like to see improved...',
      optional: true
    }
  ];

  const checkExistingSubmission = useCallback(async () => {
    try {
      const status = await surveyService.checkSurveyStatus(userEmail);
      if (status.has_submitted) {
        setHasSubmitted(true);
        setError('You have already submitted the survey. Thank you for your feedback!');
      }
    } catch (err) {
      console.log('User status check:', err.message);
    }
  }, [userEmail]);

  useEffect(() => {
    if (userEmail && currentStep > 0) {
      checkExistingSubmission();
    }
  }, [userEmail, currentStep, checkExistingSubmission]);

  const handleNext = () => {
    if (currentStep === 0) {
      // Email is now optional, but validate format if provided
      if (userEmail.trim() && !/\S+@\S+\.\S+/.test(userEmail)) {
        setError('Please enter a valid email address');
        return;
      }
    } else {
      const currentQuestion = questions[currentStep];
      if (currentQuestion.type === 'checkbox') {
        // For checkbox questions, check if at least one option is selected
        if (!responses[currentQuestion.id] || responses[currentQuestion.id].length === 0) {
          setError('Please select at least one answer to continue');
          return;
        }
      } else if (currentQuestion.type !== 'textarea' && !responses[currentQuestion.id]) {
        setError('Please select an answer to continue');
        return;
      }
    }

    setError('');
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };

  const handleResponseChange = (questionId, value, isCheckbox = false) => {
    if (isCheckbox) {
      setResponses(prev => {
        const currentValues = prev[questionId] || [];
        const newValues = currentValues.includes(value)
          ? currentValues.filter(v => v !== value) // Remove if already selected
          : [...currentValues, value]; // Add if not selected
        return {
          ...prev,
          [questionId]: newValues
        };
      });
    } else {
      setResponses(prev => ({
        ...prev,
        [questionId]: value
      }));
    }
    setError('');
  };

  const handleSubmit = async () => {
    if (hasSubmitted) {
      setError('You have already submitted the survey.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const surveyData = {
        user_email: userEmail,
        ...responses
      };

      await surveyService.submitSurvey(surveyData);
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderQuestion = () => {
    if (currentStep === 0) {
      return (
        <div className="survey-question">
          <input
            type="email"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            placeholder={questions[0].placeholder}
            className="survey-email-input"
            disabled={loading}
            style={{
              width: '100%',
              padding: '16px 20px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '2px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              color: '#ffffff',
              fontSize: '16px',
              transition: 'all 0.3s ease'
            }}
          />
        </div>
      );
    }

    const question = questions[currentStep];
    const currentValue = responses[question.id];

    switch (question.type) {
      case 'radio':
        return (
          <div className="survey-question">
            <div className="survey-options" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {question.options.map((option) => (
                <label
                  key={option.value}
                  className={`survey-option ${currentValue === option.value ? 'selected' : ''}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px 20px',
                    background: currentValue === option.value ? 'rgba(16, 185, 129, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                    border: `2px solid ${currentValue === option.value ? '#10b981' : 'rgba(255, 255, 255, 0.1)'}`,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <input
                    type="radio"
                    name={question.id}
                    value={option.value}
                    checked={currentValue === option.value}
                    onChange={(e) => handleResponseChange(question.id, e.target.value)}
                    disabled={loading || hasSubmitted}
                    style={{ display: 'none' }}
                  />
                  <span style={{ fontSize: '20px', marginRight: '12px' }}>{option.emoji}</span>
                  <span style={{ color: '#ffffff', fontSize: '16px', fontWeight: '500' }}>{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'checkbox':
        return (
          <div className="survey-question">
            <div className="survey-options" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {question.options.map((option) => {
                const isSelected = currentValue && currentValue.includes(option.value);
                return (
                  <label
                    key={option.value}
                    className={`survey-option ${isSelected ? 'selected' : ''}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '16px 20px',
                      background: isSelected ? 'rgba(16, 185, 129, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                      border: `2px solid ${isSelected ? '#10b981' : 'rgba(255, 255, 255, 0.1)'}`,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <input
                      type="checkbox"
                      name={question.id}
                      value={option.value}
                      checked={isSelected}
                      onChange={(e) => handleResponseChange(question.id, e.target.value, true)}
                      disabled={loading || hasSubmitted}
                      style={{ display: 'none' }}
                    />
                    <span style={{ fontSize: '20px', marginRight: '12px' }}>{option.emoji}</span>
                    <span style={{ color: '#ffffff', fontSize: '16px', fontWeight: '500' }}>{option.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        );

      case 'nps':
        return (
          <div className="survey-question">
            <div className="nps-container" style={{ textAlign: 'center' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                <span>{question.scale.labels[0]}</span>
                <span>{question.scale.labels[1]}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '4px', marginBottom: '16px', flexWrap: 'wrap' }}>
                {Array.from({ length: 11 }, (_, i) => (
                  <label key={i} style={{ flex: 1, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name={question.id}
                      value={i}
                      checked={currentValue === i}
                      onChange={(e) => handleResponseChange(question.id, parseInt(e.target.value))}
                      disabled={loading || hasSubmitted}
                      style={{ display: 'none' }}
                    />
                    <span style={{
                      display: 'block',
                      width: '36px',
                      height: '36px',
                      lineHeight: '36px',
                      textAlign: 'center',
                      background: currentValue === i ? '#10b981' : 'rgba(255, 255, 255, 0.05)',
                      border: `2px solid ${currentValue === i ? '#10b981' : 'rgba(255, 255, 255, 0.1)'}`,
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontWeight: '600',
                      fontSize: '14px',
                      transition: 'all 0.3s ease',
                      minWidth: '36px',
                      boxSizing: 'border-box'
                    }}>{i}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 'textarea':
        return (
          <div className="survey-question">
            <textarea
              value={currentValue || ''}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
              placeholder={question.placeholder}
              rows={4}
              disabled={loading || hasSubmitted}
              style={{
                width: '100%',
                padding: '16px 20px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                color: '#ffffff',
                fontSize: '16px',
                fontFamily: 'inherit',
                resize: 'vertical',
                minHeight: '120px',
                transition: 'all 0.3s ease'
              }}
            />
          </div>
        );

      default:
        return null;
    }
  };

  if (submitted) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '24px',
          padding: '40px',
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center'
        }}>
          <h1 style={{ color: '#ffffff', fontSize: '28px', fontWeight: '700', margin: '0 0 8px 0' }}>Thank You! 🎉</h1>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '16px', margin: '0 0 32px 0' }}>Your feedback has been submitted successfully</p>
          <div style={{ fontSize: '48px', marginBottom: '24px' }}>✅</div>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '16px', lineHeight: '1.6', margin: '16px 0' }}>We truly appreciate you taking the time to help us improve PrepLetter.</p>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '16px', lineHeight: '1.6', margin: '16px 0' }}>Your insights will help us create better content and features for everyone!</p>
        </div>
      </div>
    );
  }

  if (hasSubmitted) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '24px',
          padding: '40px',
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center'
        }}>
          <h1 style={{ color: '#ffffff', fontSize: '28px', fontWeight: '700', margin: '0 0 8px 0' }}>Already Submitted 📝</h1>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '16px', margin: '0 0 32px 0' }}>You have already completed our survey</p>
          <div style={{ fontSize: '48px', marginBottom: '24px' }}>ℹ️</div>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '16px', lineHeight: '1.6', margin: '16px 0' }}>Thank you for your previous feedback! We can only accept one response per user.</p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif'
    }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '24px',
        padding: '40px',
        maxWidth: '600px',
        width: '100%',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)'
      }}>
        {/* Progress Bar */}
        <div style={{ marginBottom: '32px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ flex: 1, height: '8px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              background: 'linear-gradient(90deg, #10b981, #3b82f6)',
              borderRadius: '4px',
              width: `${progress}%`,
              transition: 'width 0.3s ease'
            }}></div>
          </div>
          <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', fontWeight: '500', minWidth: '80px', textAlign: 'right' }}>
            {currentStep + 1} of {questions.length}
          </span>
        </div>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1>{currentQuestion.icon}</h1>
          <h1 style={{
            color: '#ffffff',
            fontSize: '28px',
            fontWeight: '700',
            margin: '0 0 8px 0',
            background: 'linear-gradient(135deg, #10b981, #3b82f6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>{currentQuestion.title}</h1>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '16px', margin: '0' }}>{currentQuestion.subtitle}</p>
        </div>

        {/* Body */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ color: '#ffffff', fontSize: '20px', fontWeight: '600', margin: '0 0 24px 0', lineHeight: '1.4' }}>
            {currentQuestion.question}
            {currentQuestion.optional && <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px', fontWeight: '400', marginLeft: '8px' }}>(Optional)</span>}
          </h2>

          {renderQuestion()}

          {error && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              padding: '12px 16px',
              color: '#fca5a5',
              fontSize: '14px',
              marginTop: '16px'
            }}>{error}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
          {currentStep > 0 && (
            <button
              onClick={handlePrevious}
              disabled={loading}
              style={{
                padding: '14px 28px',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff'
              }}
            >
              Previous
            </button>
          )}

          {currentStep < questions.length - 1 ? (
            <button
              onClick={handleNext}
              disabled={loading || hasSubmitted}
              style={{
                padding: '14px 28px',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                background: 'linear-gradient(135deg, #10b981, #3b82f6)',
                color: '#ffffff',
                marginLeft: 'auto'
              }}
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading || hasSubmitted}
              style={{
                padding: '14px 28px',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                background: 'linear-gradient(135deg, #10b981, #3b82f6)',
                color: '#ffffff',
                marginLeft: 'auto'
              }}
            >
              {loading ? 'Submitting...' : 'Submit Survey'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Survey;
